import { ItemRarity } from "../../loot/shared/item-rarity"
import { ImARealitem } from "../../ui/reusable-components/inventory-item/inventory-item-helpers"
import fs from 'fs'
import WeaponAugmentSubType, { WeaponAugmentConfigData } from "../../loot/shared/weapon-augment-sub-type"
import WeaponSubType, { WeaponSubTypePrettyName } from "../../loot/shared/weapon-sub-type"
import { memoize } from "lodash"

interface WeightedAugments {
    augment: ImARealitem,
    weight: number
}

interface AutoAugmentStepResult {
    augments: ImARealitem[]
    totalWeight: number
}

const AugmentWeightRarityCoefficients = {
    [ItemRarity.COMMON]: 0.35,
    [ItemRarity.UNCOMMON]: 0.6,
    [ItemRarity.RARE]: 0.9,
    [ItemRarity.EPIC]: 1.05,
    [ItemRarity.LEGENDARY]: 1.2,
    [ItemRarity.ASTRONOMICAL]: 1.45,
}

export async function getAutoAugments(weapon: ImARealitem, alreadySelectedAugments: ImARealitem[], availableAugments: ImARealitem[]): Promise<ImARealitem[]> {
    const selectedAugments: ImARealitem[] = [...alreadySelectedAugments]

    const poolAugments = [...availableAugments]
    // remove augments that are of same type, leaving only highest level / rarity combo
    // i.e. where AugmentWeightRarityCoefficients[augment.rarityEnum] * augment.level is maximum
    poolAugments.forEach((thisAugment) => {
        let bestAugmentOfType = thisAugment
        let bestAugmentValue = thisAugment.level * AugmentWeightRarityCoefficients[thisAugment.rarityEnum]

        poolAugments.forEach((vsAugment) => {
            if (vsAugment.itemSubType === bestAugmentOfType.itemSubType) {
                const value = vsAugment.level * AugmentWeightRarityCoefficients[vsAugment.rarityEnum]
                if (value > bestAugmentValue) {
                    bestAugmentValue = value
                    poolAugments.remove(bestAugmentOfType)
                    bestAugmentOfType = vsAugment
                }
            }
        })
    })

    const result = greedyGetAutoAugmentsStep(weapon, selectedAugments, poolAugments)
    return result.augments
    // const result = await bruteForceGetAutoAugmentsStep(weapon, selectedAugments, poolAugments, 0)
    // return result.augments
}

// greedy
function greedyGetAutoAugmentsStep(weapon: ImARealitem, selectedAugments: ImARealitem[], availableAugments: ImARealitem[]): AutoAugmentStepResult {
    //  remove incompatible augments for already selected augments
    const augmentsChoosableNow = [...availableAugments].filter((augment) => {
        const alreadyEquipped = selectedAugments.find((selected) => selected.itemSubType === augment.itemSubType)
        return !alreadyEquipped
    })

    // if no available augments: we're done
    if (augmentsChoosableNow.length === 0 || selectedAugments.length === weapon.augmentLimit) {
        let totalWeight = 0
        for (let i = 0; i < selectedAugments.length; ++i) {
            totalWeight += getAugmentWeight(selectedAugments[i], weapon, selectedAugments, i)
        }
        return { totalWeight, augments: selectedAugments }
    }

    // sort by weight
    const sortedAugmentsByWeight: WeightedAugments[] = []
    for (let i = 0; i < augmentsChoosableNow.length; ++i) {
        const augment = augmentsChoosableNow[i]
        const weight = getAugmentWeight(augment, weapon, selectedAugments)

        if (sortedAugmentsByWeight.length === 0) {
            sortedAugmentsByWeight.push({ augment, weight })
        } else {
            let index = 0
            let curWeight = sortedAugmentsByWeight[index].weight

            while (curWeight > weight && index + 1 < sortedAugmentsByWeight.length) {
                index++
                curWeight = sortedAugmentsByWeight[index].weight
            }

            sortedAugmentsByWeight.splice(index, 0, { augment, weight })
        }
    }

    // take the highest weight, if there is a tie we need to choose between them
    const selectableAugments: WeightedAugments[] = []
    let selectedIndex = 0
    while (selectedIndex === 0 || (selectedIndex < sortedAugmentsByWeight.length && sortedAugmentsByWeight[selectedIndex].weight === sortedAugmentsByWeight[selectedIndex - 1].weight)) {
        selectableAugments.push(sortedAugmentsByWeight[selectedIndex])
        selectedIndex++
    }

    let maxFoundWeight = 0
    const maxFoundWeightResults: AutoAugmentStepResult[] = []
    for (let i = 0; i < selectableAugments.length; ++i) {
        const possibleAugment = selectableAugments[i]
        const result = greedyGetAutoAugmentsStep(weapon, [...selectedAugments, possibleAugment.augment], augmentsChoosableNow)
        if (result.totalWeight > maxFoundWeight) {
            maxFoundWeight = result.totalWeight
            maxFoundWeightResults.length = 0
            maxFoundWeightResults.push(result)
        } else if (result.totalWeight === maxFoundWeight) {
            maxFoundWeightResults.push(result)
        }
    }

    const finalResult = maxFoundWeightResults[Math.getRandomInt(0, maxFoundWeightResults.length - 1)]
    return finalResult
}

function getAugmentWeight(augment: ImARealitem, weapon: ImARealitem, equippedAugments: ImARealitem[], skipIndex?: number): number {
    const rarityMod = AugmentWeightRarityCoefficients[augment.rarityEnum]
    const result = augment.level * rarityMod * getMetaModifier(augment, weapon, equippedAugments, skipIndex)

    return result
}

function getWeaponAugmentMetaValue(prospectiveSubType: WeaponSubType, augmentSubType: WeaponAugmentSubType): number {
    if (WEAPON_VS_AUGMENT_META_VALUE[prospectiveSubType] === undefined || WEAPON_VS_AUGMENT_META_VALUE[prospectiveSubType][augmentSubType] === undefined) {
        if (process.env.NODE_ENV === 'local') {
            console.warn(`Auto-augmenting: Meta value missing for weapon+augment combo: weapon ${WeaponSubType[prospectiveSubType]}, augment ${WeaponAugmentSubType[augmentSubType]}. Please see auto-augment-functions.ts to fix.`)
        }
        return 1.0
    }
    return WEAPON_VS_AUGMENT_META_VALUE[prospectiveSubType][augmentSubType]
}

function getAugmentAugmentMetaValue(prospectiveSubType: WeaponAugmentSubType, augmentSubType: WeaponAugmentSubType): number {
    if (AUGMENT_VS_AUGMENT_META_VALUE[prospectiveSubType] === undefined || AUGMENT_VS_AUGMENT_META_VALUE[prospectiveSubType][augmentSubType] === undefined) {
        if (process.env.NODE_ENV === 'local') {
            console.warn(`Auto-augmenting: Meta value missing for augment+augment combo: augment ${WeaponAugmentSubType[prospectiveSubType]}, equipped augment ${WeaponAugmentSubType[augmentSubType]}. Please see auto-augment-functions.ts to fix.`)
        }
        return 1.0
    }
    return AUGMENT_VS_AUGMENT_META_VALUE[prospectiveSubType][augmentSubType]
}

// use memoize so we only log our warnings once
const getWeaponAugmentMetaValueMemo = memoize(getWeaponAugmentMetaValue, (...args) => JSON.stringify(args))
const getAugmentAugmentMetaValueMemo = memoize(getAugmentAugmentMetaValue, (...args) => JSON.stringify(args))

function getMetaModifier(augment: ImARealitem, weapon: ImARealitem, equippedAugments: ImARealitem[], skipIndex?: number): number {
    const weaponMeta: number = getWeaponAugmentMetaValueMemo(weapon.itemSubType, augment.itemSubType)
    let augmentMeta = 1
    for (let i = 0; i < equippedAugments.length; ++i) {
        if (i !== skipIndex) {
            augmentMeta *= getAugmentAugmentMetaValueMemo(augment.itemSubType, equippedAugments[i].itemSubType)
        }
    }

    return weaponMeta * augmentMeta
}

// not used
async function bruteForceGetAutoAugmentsStep(weapon: ImARealitem, selectedAugments: ImARealitem[], availableAugments: ImARealitem[], depth: number): Promise<AutoAugmentStepResult> {
    // if no available augments: we're done
    if (availableAugments.length === 0 || selectedAugments.length === weapon.augmentLimit) {
        let totalWeight = 0
        for (let i = 0; i < selectedAugments.length; ++i) {
            totalWeight += getAugmentWeight(selectedAugments[i], weapon, selectedAugments, i)
        }
        return { totalWeight, augments: selectedAugments }
    }

    // go through each augment combination, see what weight we could get by picking it
    const comboLengthToFind = Math.min(availableAugments.length, weapon.augmentLimit - selectedAugments.length)
    // this 'combinations' function is bugged and returns wayyyyyy too many combinations
    // (it returns combos of length greater than comboLengthToFind)
    const combinationsOfAugments: ImARealitem[][] = combinations(availableAugments, comboLengthToFind, comboLengthToFind)

    for (let topAugmentIndex = 0; topAugmentIndex < availableAugments.length; ++topAugmentIndex) {
        const combo = []
        for (let innerAugIndex = topAugmentIndex; innerAugIndex < comboLengthToFind; ++innerAugIndex) {
            const augment = availableAugments[innerAugIndex]
            combo.push(augment)
        }
    }

    const STEP_WAIT = 250
    let maxFoundWeight = 0
    const maxFoundWeightResults: AutoAugmentStepResult[] = []
    for (let i = 0; i < combinationsOfAugments.length; ++i) {
        const combo = combinationsOfAugments[i]
        combo.concat(selectedAugments)

        if (combo.length > weapon.augmentLimit) {
            continue //the 'combinations' function is bugged and returns wayyyyyy too many combinations
        }

        if (i % STEP_WAIT === 0) {
            await getWaitNextFramePromise()
        }

        let thisComboWeight = 0
        for (let j = 0; j < combo.length; ++j) {
            thisComboWeight += getAugmentWeight(combo[j], weapon, combo, j)
        }

        if (thisComboWeight > maxFoundWeight) {
            maxFoundWeight = thisComboWeight
            maxFoundWeightResults.length = 0
            maxFoundWeightResults.push({ totalWeight: thisComboWeight, augments: combo })
        } else if (thisComboWeight === maxFoundWeight) {
            maxFoundWeightResults.push({ totalWeight: thisComboWeight, augments: combo })
        }
    }

    const finalResult = maxFoundWeightResults[Math.getRandomInt(0, maxFoundWeightResults.length - 1)]
    return finalResult
}

export function printAugmentToAugmentMetaValue() {
    const finalMap = {}

    const fileString = fs.readFileSync('./augmentToAugmentAutoMeta.csv', { encoding: 'utf8' })
    const lines = fileString.split('\n')

    // types
    const typeByColumn: WeaponAugmentSubType[] = []
    const firstLine = lines[0].split(',')
    for (let i = 1; i < firstLine.length; ++i) {
        const augmentType = findAugmentType(firstLine[i].trim())
        if (!augmentType) {
            throw Error("Could not match type from '" + firstLine[i].trim() + "'")
        }
        typeByColumn.push(augmentType)
    }

    // building the actual table
    for (let lineNumber = 1; lineNumber < lines.length; ++lineNumber) {
        const line = lines[lineNumber].split(',')
        const augmentType = findAugmentType(line[0])

        if (augmentType !== typeByColumn[lineNumber - 1]) {
            throw Error("Row x Column types did not match. line: " + lineNumber + " " + augmentType + " vs " + typeByColumn[lineNumber - 1])
        }

        for (let columnNumber = lineNumber + 1; columnNumber < line.length; ++columnNumber) {
            const vsAugmentType = typeByColumn[columnNumber - 1]
            const value = Number.parseFloat(line[columnNumber])

            console.log(`${augmentType} vs ${vsAugmentType}: ${value}`)

            if (!finalMap[augmentType]) {
                finalMap[augmentType] = {}
            }
            if (!finalMap[vsAugmentType]) {
                finalMap[vsAugmentType] = {}
            }

            finalMap[augmentType][vsAugmentType] = value
            finalMap[vsAugmentType][augmentType] = value
        }
    }

    console.log("final map:")
    console.log(JSON.stringify(finalMap))
}

function findAugmentType(name: string): WeaponAugmentSubType {
    let foundSubType
    WeaponAugmentConfigData.forEach((value, subType) => {
        if (value.prettyName === name) {
            foundSubType = subType
            return
        }
    })

    return foundSubType
}

export function printWeaponToAugmentMetaValue() {
    const finalMap = {}

    const fileString = fs.readFileSync('./augmentToWeaponAutoMeta.csv', { encoding: 'utf8' })
    const lines = fileString.split('\n')

    // weapon types
    const weaponByColumn: WeaponSubType[] = []
    const firstLine = lines[0].split(',')
    for (let i = 1; i < firstLine.length; ++i) {
        const weaponType = findWeaponType(firstLine[i].trim())
        if (!weaponType) {
            throw Error("Could not match type from '" + firstLine[i].trim() + "'")
        }
        weaponByColumn.push(weaponType)
    }

    // building the actual table
    for (let lineNumber = 1; lineNumber < lines.length; ++lineNumber) {
        const line = lines[lineNumber].split(',')
        const augmentType = findAugmentType(line[0])

        for (let columnNumber = 1; columnNumber < line.length; ++columnNumber) {
            const vsWeaponType = weaponByColumn[columnNumber - 1]
            const value = Number.parseFloat(line[columnNumber])

            console.log(`${augmentType} vs ${vsWeaponType}: ${value}`)

            if (!finalMap[vsWeaponType]) {
                finalMap[vsWeaponType] = {}
            }

            finalMap[vsWeaponType][augmentType] = value
        }
    }

    console.log("final weapon map:")
    console.log(JSON.stringify(finalMap))
}

function findWeaponType(name: string): WeaponSubType {
    let foundSubType: WeaponSubType
    WeaponSubTypePrettyName.forEach((value, subType) => {
        if (value.toUpperCase() === name.toUpperCase()) {
            foundSubType = subType
            return
        }
    })

    return foundSubType
}

function getWaitNextFramePromise() {
    return new Promise(resolve => setTimeout(resolve, 1))
}

//copied from https://www.npmjs.com/package/combinations
// but it is bugged (theirs is too)
function combinations(array, min, max) {
    min = min || 1;
    max = max < array.length ? max : array.length;
    const fn = function (n, src, got, all) {
        if (n == 0) {
            if (got.length > 0) {
                all[all.length] = got;
            }
            return;
        }
        for (let j = 0; j < src.length; j++) {
            fn(n - 1, src.slice(j + 1), got.concat([src[j]]), all);
        }
        return;
    }
    const all = [];
    for (let i = min; i < array.length; i++) {
        fn(i, array, [], all);
    }
    if (array.length == max) all.push(array);
    return all;
}

//guess how these were generated
const AUGMENT_VS_AUGMENT_META_VALUE = JSON.parse(`{"101":{"104":1,"105":1,"106":1.1,"107":1,"110":1.1,"112":1,"113":1,"114":1,"115":1,"116":1.1,"117":0.9,"118":1,"119":0,"120":0,"121":0,"122":1,"123":0.5,"124":0.5,"125":1,"127":1.05,"128":1,"129":0.8,"130":1,"131":1,"132":1,"138":1,"208":1.1,"215":0.75,"216":1},"104":{"101":1,"105":0.5,"106":1,"107":1,"110":1,"112":1,"113":1,"114":1,"115":1,"116":1,"117":0.6,"118":0.5,"119":1,"120":1,"121":1,"122":1,"123":0.9,"124":1,"125":1,"127":1.3,"128":1,"129":0.8,"130":1,"131":1,"132":1.2,"138":0,"208":1,"215":1,"216":1},"105":{"101":1,"104":0.5,"106":1,"107":1,"110":1,"112":1,"113":1.1,"114":1,"115":1,"116":1,"117":1.1,"118":1.05,"119":0.9,"120":0.9,"121":0.9,"122":1,"123":0.9,"124":1,"125":1,"127":0.7,"128":1,"129":1.2,"130":1,"131":1,"132":0.8,"138":0,"208":1,"215":0.9,"216":0.9},"106":{"101":1.1,"104":1,"105":1,"107":1,"110":1.2,"112":1,"113":1,"114":1,"115":1,"116":1.1,"117":0.7,"118":1,"119":0,"120":0,"121":0,"122":1,"123":0.7,"124":0.7,"125":1,"127":1.05,"128":1,"129":0.8,"130":1,"131":1,"132":1,"138":1,"208":1,"215":0.6,"216":1},"107":{"101":1,"104":1,"105":1,"106":1,"110":1,"112":1,"113":0.9,"114":1,"115":1,"116":1,"117":1.2,"118":1,"119":0,"120":0,"121":0,"122":1,"123":1.1,"124":1.1,"125":0.9,"127":1,"128":1,"129":1,"130":1,"131":1,"132":1,"138":1,"208":1.1,"215":0.75,"216":1},"110":{"101":1.1,"104":1,"105":1,"106":1.2,"107":1,"112":1,"113":1,"114":1,"115":1,"116":1.2,"117":1.1,"118":1,"119":0,"120":0,"121":0,"122":1,"123":0.9,"124":0.7,"125":1,"127":1.05,"128":1,"129":1,"130":1,"131":1,"132":1.1,"138":1,"208":0.9,"215":0.6,"216":1},"112":{"101":1,"104":1,"105":1,"106":1,"107":1,"110":1,"113":1,"114":1,"115":1,"116":1,"117":1,"118":1,"119":0,"120":0,"121":0,"122":1.1,"123":1,"124":1.1,"125":1,"127":1,"128":1,"129":1,"130":1,"131":1,"132":0.9,"138":1.1,"208":0.95,"215":1,"216":1},"113":{"101":1,"104":1,"105":1.1,"106":1,"107":0.9,"110":1,"112":1,"114":1,"115":1,"116":1,"117":1.1,"118":1,"119":0,"120":0,"121":0,"122":1,"123":1,"124":1,"125":0.9,"127":1,"128":1,"129":1.1,"130":1,"131":1,"132":0.9,"138":1,"208":1.1,"215":0.9,"216":1},"114":{"101":1,"104":1,"105":1,"106":1,"107":1,"110":1,"112":1,"113":1,"115":1,"116":1,"117":1,"118":1,"119":1,"120":1,"121":1,"122":1,"123":1,"124":1,"125":1,"127":1,"128":1,"129":1.1,"130":1,"131":1,"132":1,"138":1,"208":1,"215":1,"216":0.5},"115":{"101":1,"104":1,"105":1,"106":1,"107":1,"110":1,"112":1,"113":1,"114":1,"116":1,"117":1,"118":1,"119":1,"120":1,"121":1,"122":1,"123":1,"124":1,"125":1,"127":1,"128":1,"129":1,"130":1,"131":1,"132":1,"138":1,"208":1,"215":1.05,"216":1},"116":{"101":1.1,"104":1,"105":1,"106":1.1,"107":1,"110":1.2,"112":1,"113":1,"114":1,"115":1,"117":0.7,"118":1,"119":0,"120":0,"121":0,"122":1,"123":0.7,"124":0.7,"125":1,"127":1.05,"128":1,"129":0.8,"130":1,"131":1,"132":1,"138":1,"208":1,"215":0.6,"216":1},"117":{"101":0.9,"104":0.6,"105":1.1,"106":0.7,"107":1.2,"110":1.1,"112":1,"113":1.1,"114":1,"115":1,"116":0.7,"118":1.1,"119":0.7,"120":0.7,"121":0.7,"122":1,"123":0.7,"124":1.1,"125":0.7,"127":0.9,"128":1.4,"129":0.7,"130":0.7,"131":0.7,"132":0.7,"138":1,"208":0.7,"215":1,"216":0.8},"118":{"101":1,"104":0.5,"105":1.05,"106":1,"107":1,"110":1,"112":1,"113":1,"114":1,"115":1,"116":1,"117":1.1,"119":0,"120":0,"121":0,"122":1,"123":0,"124":1,"125":1,"127":0.7,"128":1,"129":1,"130":1,"131":1,"132":1,"138":1.1,"208":0.8,"215":0,"216":1},"119":{"101":0,"104":1,"105":0.9,"106":0,"107":0,"110":0,"112":0,"113":0,"114":1,"115":1,"116":0,"117":0.7,"118":0,"120":1,"121":1,"122":1,"123":0.95,"124":1,"125":1,"127":1.1,"128":1,"129":1,"130":1,"131":1,"132":1,"138":0,"208":0,"215":0,"216":1.1},"120":{"101":0,"104":1,"105":0.9,"106":0,"107":0,"110":0,"112":0,"113":0,"114":1,"115":1,"116":0,"117":0.7,"118":0,"119":1,"121":1,"122":1,"123":0.95,"124":1,"125":1,"127":1.1,"128":1,"129":1,"130":1,"131":1,"132":1,"138":0,"208":0,"215":0,"216":1.1},"121":{"101":0,"104":1,"105":0.9,"106":0,"107":0,"110":0,"112":0,"113":0,"114":1,"115":1,"116":0,"117":0.7,"118":0,"119":1,"120":1,"122":1,"123":0.95,"124":1,"125":1,"127":1.1,"128":1,"129":1,"130":1,"131":1,"132":1,"138":0,"208":0,"215":0,"216":1.1},"122":{"101":1,"104":1,"105":1,"106":1,"107":1,"110":1,"112":1.1,"113":1,"114":1,"115":1,"116":1,"117":1,"118":1,"119":1,"120":1,"121":1,"123":0.9,"124":0.95,"125":1.1,"127":0.95,"128":1,"129":1,"130":1,"131":1.2,"132":0.9,"138":1.1,"208":0.95,"215":1,"216":1},"123":{"101":0.5,"104":0.9,"105":0.9,"106":0.7,"107":1.1,"110":0.9,"112":1,"113":1,"114":1,"115":1,"116":0.7,"117":0.7,"118":0,"119":0.95,"120":0.95,"121":0.95,"122":0.9,"124":1,"125":0.9,"127":1,"128":0.7,"129":1.4,"130":0.7,"131":0.7,"132":0.7,"138":0.3,"208":0.8,"215":1,"216":1},"124":{"101":0.5,"104":1,"105":1,"106":0.7,"107":1.1,"110":0.7,"112":1.1,"113":1,"114":1,"115":1,"116":0.7,"117":1.1,"118":1,"119":1,"120":1,"121":1,"122":0.95,"123":1,"125":0.9,"127":0.7,"128":0.7,"129":0.7,"130":1.4,"131":0.7,"132":0.7,"138":1,"208":0.8,"215":1,"216":1.1},"125":{"101":1,"104":1,"105":1,"106":1,"107":0.9,"110":1,"112":1,"113":0.9,"114":1,"115":1,"116":1,"117":0.7,"118":1,"119":1,"120":1,"121":1,"122":1.1,"123":0.9,"124":0.9,"127":0.9,"128":0.7,"129":0.7,"130":0.7,"131":1.4,"132":0.7,"138":0.9,"208":1,"215":1,"216":1},"127":{"101":1.05,"104":1.3,"105":0.7,"106":1.05,"107":1,"110":1.05,"112":1,"113":1,"114":1,"115":1,"116":1.05,"117":0.9,"118":0.7,"119":1.1,"120":1.1,"121":1.1,"122":0.95,"123":1,"124":0.7,"125":0.9,"128":0.7,"129":0.7,"130":0.7,"131":0.7,"132":1.4,"138":0.7,"208":1.2,"215":1,"216":1.2},"128":{"101":1,"104":1,"105":1,"106":1,"107":1,"110":1,"112":1,"113":1,"114":1,"115":1,"116":1,"117":1.4,"118":1,"119":1,"120":1,"121":1,"122":1,"123":0.7,"124":0.7,"125":0.7,"127":0.7,"129":1,"130":1,"131":1,"132":1,"138":0.7,"208":1,"215":1,"216":1.1},"129":{"101":0.8,"104":0.8,"105":1.2,"106":0.8,"107":1,"110":1,"112":1,"113":1.1,"114":1.1,"115":1,"116":0.8,"117":0.7,"118":1,"119":1,"120":1,"121":1,"122":1,"123":1.4,"124":0.7,"125":0.7,"127":0.7,"128":1,"130":1,"131":1,"132":1,"138":1.4,"208":1,"215":1,"216":1.1},"130":{"101":1,"104":1,"105":1,"106":1,"107":1,"110":1,"112":1,"113":1,"114":1,"115":1,"116":1,"117":0.7,"118":1,"119":1,"120":1,"121":1,"122":1,"123":0.7,"124":1.4,"125":0.7,"127":0.7,"128":1,"129":1,"131":1,"132":1,"138":0.7,"208":1,"215":1,"216":1.1},"131":{"101":1,"104":1,"105":1,"106":1,"107":1,"110":1,"112":1,"113":1,"114":1,"115":1,"116":1,"117":0.7,"118":1,"119":1,"120":1,"121":1,"122":1.2,"123":0.7,"124":0.7,"125":1.4,"127":0.7,"128":1,"129":1,"130":1,"132":1,"138":0.7,"208":1,"215":1,"216":1.1},"132":{"101":1,"104":1.2,"105":0.8,"106":1,"107":1,"110":1.1,"112":0.9,"113":0.9,"114":1,"115":1,"116":1,"117":0.7,"118":1,"119":1,"120":1,"121":1,"122":0.9,"123":0.7,"124":0.7,"125":0.7,"127":1.4,"128":1,"129":1,"130":1,"131":1,"138":0.7,"208":1,"215":1,"216":1.1},"138":{"101":1,"104":0,"105":0,"106":1,"107":1,"110":1,"112":1.1,"113":1,"114":1,"115":1,"116":1,"117":1,"118":1.1,"119":0,"120":0,"121":0,"122":1.1,"123":0.3,"124":1,"125":0.9,"127":0.7,"128":0.7,"129":1.4,"130":0.7,"131":0.7,"132":0.7,"208":1,"215":0,"216":1},"208":{"101":1.1,"104":1,"105":1,"106":1,"107":1.1,"110":0.9,"112":0.95,"113":1.1,"114":1,"115":1,"116":1,"117":0.7,"118":0.8,"119":0,"120":0,"121":0,"122":0.95,"123":0.8,"124":0.8,"125":1,"127":1.2,"128":1,"129":1,"130":1,"131":1,"132":1,"138":1,"215":1,"216":1},"215":{"101":0.75,"104":1,"105":0.9,"106":0.6,"107":0.75,"110":0.6,"112":1,"113":0.9,"114":1,"115":1.05,"116":0.6,"117":1,"118":0,"119":0,"120":0,"121":0,"122":1,"123":1,"124":1,"125":1,"127":1,"128":1,"129":1,"130":1,"131":1,"132":1,"138":0,"208":1,"216":1},"216":{"101":1,"104":1,"105":0.9,"106":1,"107":1,"110":1,"112":1,"113":1,"114":0.5,"115":1,"116":1,"117":0.8,"118":1,"119":1.1,"120":1.1,"121":1.1,"122":1,"123":1,"124":1.1,"125":1,"127":1.2,"128":1.1,"129":1.1,"130":1.1,"131":1.1,"132":1.1,"138":1,"208":1,"215":1}}`)
const WEAPON_VS_AUGMENT_META_VALUE = JSON.parse(`{"1":{"101":0,"104":1,"105":1,"106":0,"107":0,"110":0,"112":0,"113":0,"114":1,"115":1.05,"116":0,"117":1,"118":0,"119":1.1,"120":1.1,"121":1.1,"122":0.9,"123":1,"124":1,"125":1,"127":1,"128":1,"129":1,"130":1,"131":1,"132":1,"138":0,"208":0,"215":1,"216":0},"2":{"101":1.1,"104":1.1,"105":1,"106":1.2,"107":1,"110":1,"112":1,"113":1.2,"114":1,"115":1.05,"116":1.2,"117":0,"118":0,"119":0,"120":0,"121":0,"122":1,"123":1.1,"124":1.1,"125":1,"127":1,"128":1,"129":1,"130":1,"131":1,"132":1,"138":0,"208":1.2,"215":1,"216":0},"3":{"101":0.9,"104":0,"105":1.1,"106":0.8,"107":1,"110":0.95,"112":1.1,"113":1,"114":1,"115":1.05,"116":0.8,"117":1,"118":1.1,"119":0,"120":0,"121":0,"122":1,"123":0,"124":1,"125":1,"127":1,"128":1,"129":1,"130":1,"131":1,"132":1,"138":1.1,"208":0.95,"215":1,"216":0},"4":{"101":0.75,"104":1.2,"105":0.9,"106":0.6,"107":1,"110":0.6,"112":1,"113":0.9,"114":1,"115":1.05,"116":0.6,"117":1.1,"118":0,"119":0,"120":0,"121":0,"122":1,"123":1,"124":1,"125":1,"127":1,"128":1,"129":1,"130":1,"131":1,"132":1,"138":0,"208":1,"215":1,"216":1.5},"5":{"101":1,"104":1.1,"105":0.9,"106":1,"107":1,"110":1.1,"112":1,"113":1,"114":1,"115":1.05,"116":1,"117":1,"118":0,"119":0,"120":0,"121":0,"122":1,"123":1,"124":1,"125":1,"127":1,"128":1,"129":1,"130":1,"131":1,"132":1,"138":0,"208":1.1,"215":1,"216":0},"6":{"101":1,"104":0,"105":1.1,"106":0.9,"107":1.1,"110":0.95,"112":1,"113":1.1,"114":1,"115":1.05,"116":0.9,"117":1.1,"118":1.1,"119":0,"120":0,"121":0,"122":1,"123":0,"124":1,"125":1,"127":1,"128":1,"129":1,"130":1,"131":1,"132":1,"138":1.1,"208":0,"215":1,"216":0}}`)

// download google sheet as csv, use/name as file in functions above
// design numbers:
// https://docs.google.com/spreadsheets/d/1J5LxnxLSBP6VR6r-jHa-37a1i4AU5BAfN7kpwKpqeK8/edit#gid=1736579220
// 
// You have to format it like this before download as csv.  Feel free to just copy paste over or w/e:
// https://docs.google.com/spreadsheets/d/15i12DqVxxq6PckMuemOzvNLXXcqeN1Jsl5edXu0-hbY/edit#gid=0

function difference(setA, setB) {
    const _difference = new Set(setA)
    for (const _elem of setB) {
        _difference.delete(_elem)
    }
    return _difference
}

// Dynamically check the augment subtype table for lost entries, useful in dev to diagnose autoaugment crashes
function tableCheck() {
    const augmentSubTypes = new Set(Object.values(WeaponAugmentSubType).filter((key) => typeof key === 'number'))
    const weaponSubType = new Set(Object.values(WeaponSubType).filter((key) => typeof key === 'number'))
    const existingSubTypes = new Set([])
    const rightHandSideTypes = new Set([])

    console.log('WEAPON V AUGMENT')

    for (const [key, value] of Object.entries(WEAPON_VS_AUGMENT_META_VALUE)) {
        existingSubTypes.add(parseInt(key, 10))
        for (const key2 of Object.keys(value)) {
            rightHandSideTypes.add(parseInt(key2, 10))
        }


        console.log(WeaponSubType[key])
        //@ts-ignore
        console.log([...difference(augmentSubTypes, rightHandSideTypes)].map((v) => WeaponAugmentSubType[v]))
        rightHandSideTypes.clear()
    }


    console.log('Top level keys')
    //@ts-ignore sorry not sorry
    console.log([...difference(weaponSubType, existingSubTypes)].map((v) => WeaponAugmentSubType[v]))

    existingSubTypes.clear()

    console.log('AUGMENT V AUGMENT')
    console.log('Top level keys')
    //@ts-ignore sorry not sorry
    console.log([...difference(augmentSubTypes, existingSubTypes)].map((v) => WeaponAugmentSubType[v]))
    for (const [key, value] of Object.entries(AUGMENT_VS_AUGMENT_META_VALUE)) {
        existingSubTypes.add(parseInt(key, 10))
        for (const key2 of Object.keys(value)) {
            rightHandSideTypes.add(parseInt(key2, 10))
        }
        console.log(WeaponAugmentSubType[key])
        //@ts-ignore
        console.log([...difference(augmentSubTypes, rightHandSideTypes)].map((v) => WeaponAugmentSubType[v]))
        rightHandSideTypes.clear()

    }
}

// uncomment this to get a list of augments that aren't in the current table
// tableCheck()


